/**
 * LabelSelector - Component for selecting multiple labels
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Autocomplete, TextField, Box } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { labelsApi } from '../../services/labelsApi';
import { Label } from '../../types/labels';
import LabelChip from './LabelChip';
import { getTextFieldStyles, getAutocompleteStyles, getAutocompleteListboxStyles } from '../../styles/formStyles';

interface LabelSelectorProps {
  value: number[];
  onChange: (labelIds: number[]) => void;
  disabled?: boolean;
  placeholder?: string;
  label?: string;
  error?: boolean;
  helperText?: string;
  required?: boolean;
}

const LabelSelector: React.FC<LabelSelectorProps> = ({
  value,
  onChange,
  disabled = false,
  placeholder = '',
  label,
  error = false,
  helperText,
  required = false,
}) => {
  const [selectedLabels, setSelectedLabels] = useState<Label[]>([]);

  // Fetch all labels
  const { data: labelsData, isLoading } = useQuery({
    queryKey: ['labels'],
    queryFn: () => labelsApi.getLabels(),
  });

  const labels = useMemo(() => labelsData?.results || [], [labelsData?.results]);

  // Update selected labels when value changes
  useEffect(() => {
    if (value && value.length > 0 && labels.length > 0) {
      const selected = labels.filter((label) => value.includes(label.id));
      setSelectedLabels(selected);
    } else {
      setSelectedLabels([]);
    }
  }, [value, labels]);

  const handleChange = (_event: any, newValue: Label[]) => {
    setSelectedLabels(newValue);
    onChange(newValue.map((label) => label.id));
  };

  return (
    <Autocomplete<Label, true, false, false>
      multiple
      options={labels}
      value={selectedLabels}
      onChange={handleChange}
      disabled={disabled}
      loading={isLoading}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option: Label, value: Label) => option.id === value.id}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => {
          const { key, onDelete } = getTagProps({ index });
          return (
            <LabelChip
              key={key}
              label={option}
              size="small"
              onDelete={onDelete ? () => onDelete({} as React.SyntheticEvent) : undefined}
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          required={required}
          fullWidth
          margin="normal"
          sx={{
            ...getTextFieldStyles({ error }),
            "& .MuiAutocomplete-tag": {
              color: "text.primary",
            },
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id}>
          <LabelChip label={option} size="small" />
        </Box>
      )}
      fullWidth
      sx={getAutocompleteStyles()}
      ListboxProps={{
        sx: getAutocompleteListboxStyles(),
      }}
    />
  );
};

export default LabelSelector;
